
import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import logo from '@/assets/logo_EMA.png';
import {
  Zap,
  LogOut,
  Shield,
  Users,
  FileText,
  Database,
  Bell,
  CreditCard,
  Settings,
  User,
  MessageCircle,
  TrendingUp,
  Lightbulb,
  Home,
  Activity,
  Gauge,
  LayoutDashboard,
  Send,
  Building,
  DollarSign,
  Phone,
  HelpCircle,
  Thermometer,
  BookOpen,
  Crown,
  BarChart3,
  UserCheck,
  Building2,
  Wrench
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '../../contexts/AuthContext';
import { useTenantView } from '../../contexts/TenantViewContext';
import { useActingAsAdmin } from '../../hooks/useActingAsAdmin';
import { supabase } from '@/integrations/supabase/client';

export function AppSidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, isAdmin, isOwner, isConcierge, user, isPlatformAdminUser, ownedApartments, conciergeApartments } = useAuth();
  const { inTenantView } = useTenantView();
  const { setOpen } = useSidebar();
  const actingAsAdmin = useActingAsAdmin();
  const [hasUnsignedContract, setHasUnsignedContract] = useState(false);

  // Check for unsigned contracts for regular users
  useEffect(() => {
    const checkUnsignedContracts = async () => {
      if (!user || actingAsAdmin) return;

      try {
        const { data, error } = await supabase
          .from('contracts')
          .select('id, signed_at')
          .eq('user_id', user.id)
          .is('signed_at', null)
          .limit(1);

        if (!error && data && data.length > 0) {
          setHasUnsignedContract(true);
        } else {
          setHasUnsignedContract(false);
        }
      } catch (err) {
        console.error('Error checking unsigned contracts:', err);
      }
    };

    checkUnsignedContracts();
  }, [user?.id, actingAsAdmin]);

  const handleNavigation = (url: string) => {
    navigate(url);
    setOpen(false);
  };

  // Strip query string for active path comparison
  const urlPath = (url: string) => url.split('?')[0];

  const adminItems = [
    {
      title: "Dashboard",
      url: "/admin/dashboard",
      icon: LayoutDashboard,
    },
    {
      title: "User Management",
      url: "/admin/users",
      icon: Users,
    },
    {
      title: "Buildings",
      url: "/admin/buildings",
      icon: Building,
    },
    {
      title: "Building Pricing",
      url: "/admin/building-pricing",
      icon: DollarSign,
    },
    {
      title: "Smart Meters",
      url: "/admin/smart-meters",
      icon: Gauge,
    },
    {
      title: "Contract Management",
      url: "/admin/contracts",
      icon: FileText,
    },
    // {
    //   title: "System Reports",
    //   url: "/admin/reports",
    //   icon: Activity,
    // },
    {
      title: "Client Notifications",
      url: "/admin/notifyc_client",
      icon: Send,
    },
    {
      title: "Access Control",
      url: "/admin/access-control",
      icon: Shield,
    },
    {
      title: "Role Assignments",
      url: "/admin/role-assignments",
      icon: UserCheck,
    },
    {
      title: "Top-up History",
      url: "/admin/topup-history",
      icon: CreditCard,
    },
    {
      title: "Reports",
      url: "/admin/reports",
      icon: BarChart3,
    },
    {
      title: "Documentation",
      url: "/documentation?tab=admin",
      icon: BookOpen,
    }
  ];

  const superAdminItems = [
    {
      title: "Super Admins",
      url: "/superadmin",
      icon: Crown,
    },
    {
      title: "Financial Logs",
      url: "/superadmin/financial-logs",
      icon: CreditCard,
    },
    {
      title: "Activity Logs",
      url: "/superadmin/activity-logs",
      icon: Activity,
    },
    {
      title: "Bill Reports",
      url: "/superadmin/bill-reports",
      icon: BarChart3,
    },
    {
      title: "Meter Operations",
      url: "/superadmin/meter-operations",
      icon: Wrench,
    },
  ];

  const userMenuItems = [
    {
      title: "Dashboard",
      url: "/user/dashboard",
      icon: Home,
    },
    {
      title: "Notices",
      url: "/user/notices",
      icon: Bell,
    },
    {
      title: "Topup History",
      url: "/user/bills",
      icon: CreditCard,
    },
    {
      title: "Contracts",
      url: "/user/contracts",
      icon: FileText,
    },
    {
      title: "Chatbot",
      url: "/user/chatbot",
      icon: MessageCircle,
    },
    {
      title: "Consumption Evolution",
      url: "/user/consumption-evolution",
      icon: TrendingUp,
    },
    // {
    //   title: "Appliance Insights",
    //   url: "/user/appliance-insights",
    //   icon: Lightbulb,
    // },
    {
      title: "Top Up",
      url: "/user/topup",
      icon: CreditCard,
    },
    {
      title: "Contact & Support",
      url: "/user/contact-support",
      icon: Phone,
    },
    {
      title: "Documentation",
      url: "/documentation?tab=user",
      icon: BookOpen,
    },
    {
      title: "Profile & Settings",
      url: "/user/settings",
      icon: Settings,
    },
  ];

  const ownerMenuItems = [
    {
      title: "Dashboard",
      url: "/owner/dashboard",
      icon: LayoutDashboard,
    },
    {
      title: "My Units",
      url: "/owner/units",
      icon: Building2,
    },
    {
      title: "Assign Roles",
      url: "/owner/assign-roles",
      icon: Users,
    },
    {
      title: "Bills",
      url: "/owner/bills",
      icon: CreditCard,
    },
    {
      title: "Top Up",
      url: "/owner/topup",
      icon: Zap,
    },
    {
      title: "Top-up History",
      url: "/owner/topup-history",
      icon: CreditCard,
    },
    {
      title: "Contracts",
      url: "/owner/contracts",
      icon: FileText,
    },
    {
      title: "Documentation",
      url: "/documentation?tab=owner",
      icon: BookOpen,
    },
    {
      title: "Settings",
      url: "/owner/settings",
      icon: Settings,
    },
  ];

  const conciergeMenuItems = [
    {
      title: "Dashboard",
      url: "/concierge/dashboard",
      icon: LayoutDashboard,
    },
    {
      title: "My Apartments",
      url: "/concierge/apartments",
      icon: Building2,
    },
    {
      title: "Top Up",
      url: "/concierge/topup",
      icon: Zap,
    },
    {
      title: "Top-up History",
      url: "/concierge/topup-history",
      icon: CreditCard,
    },
    {
      title: "Documentation",
      url: "/documentation?tab=concierge",
      icon: BookOpen,
    },
    {
      title: "Settings",
      url: "/concierge/settings",
      icon: Settings,
    },
  ];

  return (
    <Sidebar>
      <SidebarHeader className="p-4">
        <div className="flex items-center space-x-3">
          <img src={logo} alt="Growth Energy" className="w-10 h-10 object-contain" />
          <div className="flex flex-col">
            <span className="text-sm font-bold text-gray-900 dark:text-white leading-tight">
              {actingAsAdmin ? 'Admin Panel' : !inTenantView && isOwner() ? 'Owner Portal' : !inTenantView && isConcierge() ? 'Concierge Portal' : 'Growth Energy'}
            </span>
            <span className="text-xs text-gray-500 dark:text-gray-400 leading-tight">
              Utility Intelligence for Physical Assets
            </span>
            {isPlatformAdminUser() && (
              <Badge variant="outline" className="mt-1 w-fit bg-purple-100 text-purple-800 border-purple-300 dark:bg-purple-900 dark:text-purple-200 dark:border-purple-700">
                <Crown size={12} className="mr-1" />
                Super Admin
              </Badge>
            )}
          </div>
        </div>
      </SidebarHeader>
      
      <SidebarContent>
        {actingAsAdmin ? (
          <>
            {isPlatformAdminUser() && (
              <SidebarGroup>
                <SidebarGroupLabel className="text-purple-600">Super Admin</SidebarGroupLabel>
                <SidebarGroupContent>
                  <SidebarMenu>
                    {superAdminItems.map((item) => (
                      <SidebarMenuItem key={item.title}>
                        <SidebarMenuButton
                          isActive={location.pathname === urlPath(item.url)}
                          className="data-[active=true]:bg-purple-100 data-[active=true]:text-purple-800 hover:bg-purple-50 hover:text-purple-700"
                          onClick={() => handleNavigation(item.url)}
                        >
                          <item.icon size={20} />
                          <span>{item.title}</span>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    ))}
                  </SidebarMenu>
                </SidebarGroupContent>
              </SidebarGroup>
            )}
            <SidebarGroup>
              <SidebarGroupLabel className="text-red-600">Administration</SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu>
                  {adminItems.map((item) => (
                    <SidebarMenuItem key={item.title}>
                      <SidebarMenuButton
                        isActive={location.pathname === urlPath(item.url)}
                        className="data-[active=true]:bg-red-100 data-[active=true]:text-red-800 hover:bg-red-50 hover:text-red-700"
                        onClick={() => handleNavigation(item.url)}
                      >
                        <item.icon size={20} />
                        <span>{item.title}</span>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          </>
        ) : !inTenantView && isOwner() ? (
          <SidebarGroup>
            <SidebarGroupLabel className="text-blue-600">Owner Portal</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {ownerMenuItems.map((item) => (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton
                      isActive={location.pathname === urlPath(item.url) || location.pathname.startsWith(urlPath(item.url) + '/')}
                      className="data-[active=true]:bg-blue-100 data-[active=true]:text-blue-800 hover:bg-blue-50 hover:text-blue-700"
                      onClick={() => handleNavigation(item.url)}
                    >
                      <item.icon size={20} />
                      <span>{item.title}</span>
                      {item.title === "My Units" && ownedApartments.length > 0 && (
                        <Badge variant="secondary" className="ml-auto text-xs">
                          {ownedApartments.length}
                        </Badge>
                      )}
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ) : !inTenantView && isConcierge() ? (
          <SidebarGroup>
            <SidebarGroupLabel className="text-teal-600">Concierge Portal</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {conciergeMenuItems.map((item) => (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton
                      isActive={location.pathname === urlPath(item.url) || location.pathname.startsWith(urlPath(item.url) + '/')}
                      className="data-[active=true]:bg-teal-100 data-[active=true]:text-teal-800 hover:bg-teal-50 hover:text-teal-700"
                      onClick={() => handleNavigation(item.url)}
                    >
                      <item.icon size={20} />
                      <span>{item.title}</span>
                      {item.title === "My Apartments" && conciergeApartments.length > 0 && (
                        <Badge variant="secondary" className="ml-auto text-xs">
                          {conciergeApartments.length}
                        </Badge>
                      )}
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ) : (
          <SidebarGroup>
            <SidebarGroupLabel>Main Menu</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {userMenuItems.map((item) => (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton
                      isActive={location.pathname === urlPath(item.url)}
                      onClick={() => handleNavigation(item.url)}
                    >
                      <div className="relative">
                        <item.icon size={20} />
                        {item.title === "Contracts" && hasUnsignedContract && (
                          <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-red-500 rounded-full animate-pulse" />
                        )}
                      </div>
                      <span>{item.title}</span>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}
      </SidebarContent>

      <SidebarFooter className="p-4">
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton onClick={logout}>
              <LogOut size={20} />
              <span className="text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300">
                Sign Out
              </span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  );
}
